import React, { useEffect, useState, useRef, MutableRefObject } from "react";
import { useAppSelector, useAnimations } from "../../hooks";
import classes from "../../styles/Buttons.module.css";
import { getClasses, isPointInRect } from "../../utils";
import Commander from "../../commander";

const DEFAULT_ANIMATIONS = {
  click: { duration: 0.3, main: classes["button-click"] },
  mouseDown: {
    duration: 0.3,
    scale: 0.9,
    // main: classes["button-clear"],
    // end: classes["button-shaking"],
    ease: "sine.out",
  },
  mouseUp: {
    duration: 0.2,
    delay: 0.1,
    scale: 1.0,
    // main: classes["button-clear"],
    ease: "sine.inOut",
  },
  hoverIn: {},
  hoverOut: {},
  show: { duration: 0.3, opacity: 1, cursor: "pointer" },
  hide: { duration: 0.3, opacity: 0, cursor: "auto" },
};

type Animations = {
  [animation: string]:
    | { duration: number; main?: string; end?: string } // animation definition
    | ((animation: string) => void); // animation callback
};

enum POINTER {
  none,
  mouse,
  touch,
}

export interface ButtonProps {
  name?: string;
  className?: string;
  disabled?: boolean;
  available?: boolean;
  hidden?: boolean;
  soundEffect?: string;
  clickDelay?: number; // in ms
  animations?: Animations;
  onClick?: (e?: React.MouseEvent) => void;
  onHover?: (e: React.MouseEvent) => void;
  interactionType?: "click" | "release";
}

const Button = React.forwardRef<
  HTMLDivElement,
  React.PropsWithChildren<ButtonProps>
>((props, ref?) => {
  const type = props.interactionType ?? "click";
  const [clicked, setClicked] = useState(false);
  const outer = useRef(null);
  const innerRef = useRef(null);
  const held = useRef(false);
  const pointerType = useRef(POINTER.none); // 1 - mouse, 2 - touch

  const animations = {
    ...DEFAULT_ANIMATIONS,
    ...props.animations,
  };

  const disableAnimation = {
    activate: { duration: 0.3, opacity: 1, cursor: "pointer" },
    deactivate: { duration: 0.3, opacity: 0.3, cursor: "auto" },
  };

  const inner = (ref ?? innerRef) as MutableRefObject<HTMLDivElement | null>;
  const id = `btn-${props.name || Date.now()}`;
  const disableId = `btn-disable-${props.name || Date.now()}`;
  const [animationClass, playAnimation] = useAnimations(animations, id, outer);
  // The disable/enable animations also use opacity /same as show/hide animations/,
  // so in order not to affect each other the animations are applied on different elements,
  // hance the two different uses of "useAnimations"
  const [animationDisableClass, playDisableAnimation] = useAnimations(
    disableAnimation,
    disableId,
    inner
  );
  const frozen = useAppSelector((state) => state.details.frozen);
  const disabled = frozen || props.disabled;

  useEffect(() => {
    if (clicked) {
      const delay = props.clickDelay || 300;
      const interval = setTimeout(() => setClicked(false), delay);

      return () => clearInterval(interval);
    }
  }, [clicked, props.clickDelay]);

  const onClick = (e: React.MouseEvent) => {
    if (
      disabled ||
      clicked ||
      !props.onClick ||
      props.hidden ||
      // if the e.isTrusted is false this mean that the event was triggered artificially,
      // like the spin button activated from the spacebar
      (type !== "click" && e.isTrusted)
    ) {
      held.current = false;
      return;
    }

    held.current = false;

    playAnimation("click");
    playAnimation("hoverOut");
    
    props.onClick(e);
    setClicked(true);
    if (props.soundEffect) Commander.playSound(props.soundEffect);
  };

  const onHover = (e: React.MouseEvent) => {
    if (props.onHover) props.onHover(e);
    playAnimation("hoverIn");
  };

  const buttonDown = (newPointerType: 1 | 2, extraCondition: boolean) => {
    if (
      disabled ||
      clicked ||
      props.hidden ||
      type !== "release" ||
      extraCondition
    )
      return;

    held.current = true;

    playAnimation("mouseDown");
    setClicked(true);

    pointerType.current = newPointerType;
  };

  const onMouseDown = (e: React.MouseEvent) => {
    // console.log("mouseDown");
    buttonDown(
      POINTER.mouse,
      e.button !== 0 || pointerType.current === POINTER.touch
    );
  };

  const onTouchStart = buttonDown.bind(null, POINTER.touch, false);

  const buttonUp = () => {
    if (disabled || props.hidden || !held.current || type !== "release") return;

    playAnimation("mouseUp");
    held.current = false;
    if (props.onClick) props.onClick();
  };

  const onMouseUp = (e: React.MouseEvent) => {
    // console.log("mouseUP");
    if (e.button !== 0 || pointerType.current !== POINTER.mouse) return;
    buttonUp();
  };

  const onTouchEnd = buttonUp;

  const onTouchMove = (e: React.TouchEvent) => {
    const emptyRect = { x: 0, y: 0, width: 0, height: 0 };
    const rect = inner?.current?.getBoundingClientRect() ?? emptyRect;
    const { clientX, clientY } = e.touches[0];
    const isTouchLeft = !isPointInRect(
      [clientX, clientY],
      [rect.x, rect.y, rect.width, rect.height]
    );

    if (isTouchLeft) onMouseLeave();
  };

  const onMouseLeave = (event?: any) => {
    if (!held.current) {
      // the function must be triggered by click/touch event to play the "hoverOut" animation
      if (event && pointerType.current !== POINTER.touch) playAnimation("hoverOut");
      return;
    }
    // playAnimation("hoverOut")
    playAnimation("mouseUp");
    held.current = false;
  };

  const onContextMenu = (e: React.MouseEvent) => {
     e.preventDefault();
  };

  const onTouchCancel = onMouseLeave;

  useEffect(() => {
    const animation = disabled ? "deactivate" : "activate";
    playDisableAnimation(animation);
    onMouseLeave();
  }, [disabled]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const animation = props.hidden ? "hide" : "show";
    playAnimation(animation);
  }, [props.hidden]); // eslint-disable-line react-hooks/exhaustive-deps

  if (typeof props.available === "boolean" && !props.available) return null;

  return (
    <div
      className={getClasses(
        classes,
        animationClass,
        "button-outer",
        props.className
      )}
      ref={outer}
    >
      <div
        ref={inner}
        onClick={onClick}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchCancel}
        onTouchMove={onTouchMove}
        onMouseEnter={onHover}
        onMouseDown={onMouseDown}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseLeave}
        onContextMenu={onContextMenu}
        className={getClasses(
          classes,
          "button",
          props.className,
          animationClass,
          animationDisableClass,
          disabled ? "disabled" : ""
        )}
      >
        {props.children}
      </div>
    </div>
  );
});

export default Button;
